import React, { useEffect, useMemo } from "react";
import { clearAllBodyScrollLocks, disableBodyScroll } from "body-scroll-lock";

import EmptyCart from "core/Components/CartPopup/EmptyCart";
import CartPopupCloseBar from "core/Components/CartPopup/CartPopupCloseBar";
import CartPopupContainer from "core/Components/CartPopup/CartPopupContainer";
import CartPopupContent from "core/Components/CartPopup/CartPopupContent";
import CartPopupSection from "core/Components/CartPopup/CartPopupSection";
import LayerToBlockMain from "core/Components/CartPopup/LayerToBlockMain";
import CartPopupFooter from "core/Components/CartPopup/Footer/CartPopupFooter";
import CartPopupGoToCartButton from "core/Components/CartPopup/Footer/CartPopupGoToCartButton";
import CartPopupSelectedItemCount from "core/Components/CartPopup/Footer/CartPopupSelectedItemCount";
import CartPopupSubTotal from "core/Components/CartPopup/Footer/CartPopupSubTotal";
import CartPopupHeader from "core/Components/CartPopup/Header/CartPopupHeader";
import CartPopupHeaderCurrency from "core/Components/CartPopup/Header/CartPopupHeaderCurrency";
import CartPopupHeaderIcon from "core/Components/CartPopup/Header/CartPopupHeaderIcon";
import CartPopupHeaderTitle from "core/Components/CartPopup/Header/CartPopupHeaderTitle";
import CartImageAndTitleContainer from "core/Components/CartPopup/Main/CartImageAndTitleContainer";
import CartPopupItemContainer from "core/Components/CartPopup/Main/CartItemContainer";
import CartItemContent from "core/Components/CartPopup/Main/CartItemContent";
import CartItemImage from "core/Components/CartPopup/Main/CartItemImage";
import CartItemQuantityAndPrice from "core/Components/CartPopup/Main/CartItemQuantityAndPrice";
import CartItemRemoveButton from "core/Components/CartPopup/Main/CartItemRemoveButton";
import CartItemTitle from "core/Components/CartPopup/Main/CartItemTitle";
import CartPopupMain from "core/Components/CartPopup/Main/CartPopupMain";
import { useAppState } from "core/contexts/AppContext";
import useBreakpoint from "core/hooks/useBreakpoint";
import useHeaderHeight from "core/hooks/useHeaderHeight";
import document from "core/includes/document";
import CartPromoCodes from "core/Components/CartPopup/Main/CartPromoCodes";
import { useCountryAndCurrency } from "core/hooks/useCountryAndCurrency";
import useCart from "pages/Components/CartPage/hooks/useCart";
import { isBetween, isOnly, isUp } from "theme/breakpoints";

type Props = {
    showBreadcrumb: boolean,
    hideHeaderCountry: boolean,
};

const CartPopup: React.FC<Props> = ({ showBreadcrumb, hideHeaderCountry }) => {
    const [appState] = useAppState();
    const { updateCart, getCountry } = useCart();

    const headerHeight = useHeaderHeight({ showBreadcrumb, hideHeaderCountry });
    const { breakpoint } = useBreakpoint();
    const boxWidth = useMemo(() => {
        switch (true) {
            case isUp(breakpoint, 'xxl'):
                return 363;
            case isOnly(breakpoint, 'xl'):
                return 342;
            case isBetween(breakpoint, 'sm', 'lg'):
                return 310;
            default:
                return 301;
        }
    }, [breakpoint]);

    const subtotalPrice = appState.cart?.subtotalAmount ?? 0;
    const totalTax = appState.cart?.taxAmount ?? 0;
    const countryCode = getCountry();
    const { currency } = useCountryAndCurrency(countryCode?.code);
    const cartItems = appState.cart?.products || [];
    const hasItems = (cartItems?.length ?? 0) > 0;

    useEffect(() => {
        if (appState.isOpenCartPopup) {
            disableBodyScroll(document.body);

            return () => {
                clearAllBodyScrollLocks();
            }
        }
    }, [appState.isOpenCartPopup]);

    return (
        <>
            <CartPopupSection headerHeight={headerHeight}>
                <CartPopupContainer>
                    <CartPopupCloseBar headerHeight={headerHeight} />
                    <CartPopupContent headerHeight={headerHeight}>
                        <CartPopupHeader width={boxWidth}>
                            <CartPopupHeaderIcon />
                            <CartPopupHeaderTitle />
                            <CartPopupHeaderCurrency currency={currency} />
                        </CartPopupHeader>
                        <CartPopupMain hasItems={hasItems}>
                            {hasItems ? cartItems.map((product: CartProduct) => {
                                const to = product.type === 'event' ? (
                                    '/event-registration/' + product.id
                                ) : (
                                    '/classroom-resources/' + product.urlSegment
                                );

                                return (
                                    <CartPopupItemContainer key={product.id} width={boxWidth}>
                                        <CartItemContent>
                                            <CartImageAndTitleContainer>
                                                {product.image?.URL && (
                                                    <CartItemImage
                                                        to={to}
                                                        url={product.image.URL}
                                                    />
                                                )}
                                                <CartItemTitle
                                                    to={to}
                                                    tippyContent={product.title}
                                                >
                                                    {product.title}
                                                </CartItemTitle>
                                            </CartImageAndTitleContainer>
                                            <CartItemQuantityAndPrice
                                                quantity={product.quantity}
                                                totalPrice={product.totalPrice}
                                                discountPrice={product.discountedPrice}
                                                isTaxable
                                            />
                                        </CartItemContent>
                                        <CartItemRemoveButton
                                            removeItem={
                                                () => updateCart({
                                                    productId: product.id,
                                                    registrationId: product.registrationId,
                                                    userCode: product.userCode,
                                                    isEvent: product.type === 'event',
                                                })
                                            }
                                        />
                                    </CartPopupItemContainer>
                                );
                            }) : (
                                <EmptyCart />
                            )}
                        </CartPopupMain>
                        <CartPromoCodes />
                        <CartPopupFooter width={boxWidth}>
                            <CartPopupSelectedItemCount
                                itemCount={cartItems.length}
                            />
                            <CartPopupSubTotal
                                subtotalPrice={subtotalPrice}
                                totalTax={totalTax}
                            />
                            <CartPopupGoToCartButton hasItems={hasItems} />
                        </CartPopupFooter>
                    </CartPopupContent>
                </CartPopupContainer>
            </CartPopupSection>
            <LayerToBlockMain showLayer={appState.isOpenCartPopup} />
        </>
    );
};

export default CartPopup;